import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { ROLE_PERMISSIONS, type TeamRole, type RolePermissions } from '@/types/domain/Team.types';
import { PLAN_LIMITS } from '@/constants/subscriptionPlans';

// ============================================================================
// Types
// ============================================================================

export interface Brand {
  brandId: string;
  name: string;
  handle: string | null;
  defaultCurrency: string;
  timezone: string;
  protocolSchemaVersion: string;
  ownerUserId: string;
  displayOrder: number;
  accentColor: string;
  /**
   * The year the business started, as stated by the owner. NULL means not
   * stated — never inferred from createdAt, which is only when the row was
   * inserted. The two answer different questions and must not be conflated.
   */
  foundedYear: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrandMembership {
  memberId: string;
  brandId: string;
  role: string;
  status: string;
  brand: Brand;
}

interface BrandContextType {
  // State
  activeBrand: Brand | null;
  brands: BrandMembership[];
  userRole: TeamRole | null;
  permissions: RolePermissions | null;
  loading: boolean;
  error: string | null;
  needsOnboarding: boolean;

  // Actions
  switchBrand: (brandId: string) => void;
  refreshBrands: () => Promise<void>;
  createBrand: (name: string, currency?: string, handle?: string) => Promise<Brand>;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

// ============================================================================
// Hook
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

// ============================================================================
// Provider
// ============================================================================

const ACTIVE_BRAND_KEY = 'contruly-active-brand-id';

export const BrandProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeBrandId, setActiveBrandId] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_BRAND_KEY); } catch { return null; }
  });

  const { data, error: queryError } = useQuery({
    queryKey: user?.id ? queryKeys.brands(user.id) : ['contruly', 'brands', 'no-user'],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user) return [] as BrandMembership[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- v2 tables not yet in generated Supabase types
      const client = supabase as any;

      const { data, error: fetchError } = await client
        .from('v2_brand_members')
        .select(`
          member_id,
          brand_id,
          role,
          status,
          v2_brands!inner (
            brand_id,
            name,
            handle,
            default_currency,
            timezone,
            protocol_schema_version,
            owner_user_id,
            display_order,
            accent_color,
            founded_year,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('display_order', { ascending: true, referencedTable: 'v2_brands' });

      if (fetchError) throw fetchError;

      interface BrandMemberRow {
        member_id: string;
        brand_id: string;
        role: string;
        status: string;
        v2_brands: {
          brand_id: string;
          name: string;
          handle: string | null;
          default_currency: string;
          timezone: string;
          protocol_schema_version: string;
          owner_user_id: string;
          display_order: number;
          accent_color: string | null;
          founded_year: number | null;
          created_at: string;
          updated_at: string;
        };
      }

      const memberships: BrandMembership[] = ((data || []) as BrandMemberRow[]).map((row) => ({
        memberId: row.member_id,
        brandId: row.brand_id,
        role: row.role,
        status: row.status,
        brand: {
          brandId: row.v2_brands.brand_id,
          name: row.v2_brands.name,
          handle: row.v2_brands.handle || null,
          defaultCurrency: row.v2_brands.default_currency,
          timezone: row.v2_brands.timezone,
          protocolSchemaVersion: row.v2_brands.protocol_schema_version,
          ownerUserId: row.v2_brands.owner_user_id,
          displayOrder: row.v2_brands.display_order ?? 0,
          accentColor: row.v2_brands.accent_color || '#184baa',
          foundedYear: row.v2_brands.founded_year ?? null,
          createdAt: row.v2_brands.created_at,
          updatedAt: row.v2_brands.updated_at,
        },
      }));

      // Auto-select brand if not already chosen. localStorage → DB preference → first by display_order.
      if (memberships.length > 0) {
        let savedId: string | null = null;
        try { savedId = localStorage.getItem(ACTIVE_BRAND_KEY); } catch { /* ignore */ }

        const localMatch = savedId ? memberships.find(m => m.brandId === savedId) : null;

        if (localMatch) {
          setActiveBrandId(localMatch.brandId);
        } else {
          let dbBrandId: string | null = null;
          try {
            const { data: profile } = await client
              .from('profiles')
              .select('last_active_brand_id')
              .eq('user_id', user.id)
              .maybeSingle();
            dbBrandId = profile?.last_active_brand_id ?? null;
          } catch { /* ignore */ }

          const dbMatch = dbBrandId ? memberships.find(m => m.brandId === dbBrandId) : null;
          if (dbMatch) {
            setActiveBrandId(dbMatch.brandId);
            try { localStorage.setItem(ACTIVE_BRAND_KEY, dbMatch.brandId); } catch { /* */ }
          } else {
            const firstId = memberships[0].brandId;
            setActiveBrandId(firstId);
            try { localStorage.setItem(ACTIVE_BRAND_KEY, firstId); } catch { /* */ }
          }
        }
      } else {
        setActiveBrandId(null);
        try { localStorage.removeItem(ACTIVE_BRAND_KEY); } catch { /* */ }
      }

      return memberships;
    },
  });

  // `data === undefined` is the single semantic for "we don't know yet" — covers
  // auth still loading (query disabled), PersistQueryClientProvider restoring
  // the cache (query paused), and the first fetch. Using `isLoading` here would
  // flash the BrandOnboarding screen on hard refresh: during cache restore the
  // query is paused (`isFetching: false`) with no data yet, so `isLoading` is
  // false but `data` is undefined → `needsOnboarding` would falsely be true.
  const brands = data ?? [];
  const loading = data === undefined;
  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load brands') : null;

  // refreshBrands: kept for API compatibility. Most callers no longer need
  // to call this — the RealtimeSupervisor invalidates queryKeys.brands on
  // every v2_brands change, which triggers automatic refetch.
  const refreshBrands = useCallback(async () => {
    if (!user?.id) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.brands(user.id) });
  }, [queryClient, user?.id]);

  const switchBrand = useCallback((brandId: string) => {
    setActiveBrandId(brandId);
    try { localStorage.setItem(ACTIVE_BRAND_KEY, brandId); } catch { /* localStorage unavailable */ }

    // Fire-and-forget DB persistence for cross-device sync
    if (user?.id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any)
        .from('profiles')
        .update({ last_active_brand_id: brandId })
        .eq('user_id', user.id)
        .then(() => { /* intentionally ignored */ });
    }
  }, [user?.id]);

  const createBrand = useCallback(async (name: string, currency: string = 'EUR', handle?: string): Promise<Brand> => {
    if (!user) throw new Error('Not authenticated');

    // Enforce max brands limit
    const maxBrands = PLAN_LIMITS.pro.maxBrands;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- v2 tables not yet in generated Supabase types
    const client = supabase as any;

    const { count, error: countError } = await client
      .from('v2_brands')
      .select('brand_id', { count: 'exact', head: true })
      .eq('owner_user_id', user.id)
      .is('deleted_at', null);

    if (countError) throw countError;
    if ((count ?? 0) >= maxBrands) {
      throw new Error(`You can have up to ${maxBrands} brands on your current plan.`);
    }

    const { data, error: insertError } = await client
      .from('v2_brands')
      .insert({
        name,
        default_currency: currency,
        owner_user_id: user.id,
        ...(handle ? { handle } : {}),
      })
      .select()
      .single();

    if (insertError) throw insertError;

    const newBrand: Brand = {
      brandId: data.brand_id,
      name: data.name,
      handle: data.handle || null,
      defaultCurrency: data.default_currency,
      timezone: data.timezone,
      protocolSchemaVersion: data.protocol_schema_version,
      ownerUserId: data.owner_user_id,
      displayOrder: data.display_order ?? 0,
      accentColor: data.accent_color || '#184baa',
      foundedYear: data.founded_year ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };

    // Refresh brands list and switch to new brand
    if (user?.id) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.brands(user.id) });
    }
    switchBrand(newBrand.brandId);

    return newBrand;
  }, [user, queryClient, switchBrand]);

  // Derived state
  const activeMembership = brands.find(m => m.brandId === activeBrandId) || null;
  const activeBrand = activeMembership?.brand || null;
  const userRole = (activeMembership?.role as TeamRole) || null;
  const permissions = userRole ? ROLE_PERMISSIONS[userRole] || null : null;
  const needsOnboarding = data !== undefined && brands.length === 0;

  return (
    <BrandContext.Provider
      value={{
        activeBrand,
        brands,
        userRole,
        permissions,
        loading,
        error,
        needsOnboarding,
        switchBrand,
        refreshBrands,
        createBrand,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};
